// The few routes of the web client, in the path like docs/web-designs shows them:
// /c/<conversation>, /c/<conversation>/thema/<topic>, /einstellungen, /voxie, /spiele.

import { useSyncExternalStore } from "react";

export type Route =
  | { screen: "chats"; conversation?: string; topic?: string; message?: string; panel?: "boards" | "documents" }
  | { screen: "settings" }
  | { screen: "voxie" }
  | { screen: "games" }
  | { screen: "board"; id: string }
  | { screen: "document"; id: string };

export function parse(path: string, search = ""): Route {
  const parts = path.split("/").filter(Boolean);
  if (parts[0] === "einstellungen") return { screen: "settings" };
  if (parts[0] === "voxie") return { screen: "voxie" };
  if (parts[0] === "spiele") return { screen: "games" };
  if (parts[0] === "canvas" && parts[1]) return { screen: "board", id: parts[1] };
  if (parts[0] === "dokument" && parts[1]) return { screen: "document", id: parts[1] };
  if (parts[0] === "c" && parts[1]) {
    const message = new URLSearchParams(search).get("m") ?? undefined;
    return { screen: "chats", conversation: parts[1], topic: parts[2] === "thema" ? parts[3] : undefined, message, panel: parts[2] === "canvas" ? "boards" : parts[2] === "dokumente" ? "documents" : undefined };
  }
  return { screen: "chats" };
}

export function format(route: Route): string {
  switch (route.screen) {
    case "settings":
      return "/einstellungen";
    case "voxie":
      return "/voxie";
    case "games":
      return "/spiele";
    case "board":
      return `/canvas/${route.id}`;
    case "document":
      return `/dokument/${route.id}`;
    case "chats":
      if (!route.conversation) return "/";
      return `/c/${route.conversation}${route.topic ? `/thema/${route.topic}` : route.panel === "boards" ? "/canvas" : route.panel === "documents" ? "/dokumente" : ""}${route.message ? `?m=${route.message}` : ""}`;
  }
}

const listeners = new Set<() => void>();
let current = parse(location.pathname, location.search);
window.addEventListener("popstate", () => {
  current = parse(location.pathname, location.search);
  listeners.forEach((l) => l());
});

export function navigate(route: Route, replace = false) {
  const url = format(route);
  if (replace) history.replaceState(null, "", url);
  else history.pushState(null, "", url);
  current = route;
  listeners.forEach((l) => l());
}

export function useRoute(): Route {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
  );
}
